import { useEffect, useMemo, useState } from 'react';
import { PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { BarcodeField } from '@/components/work/BarcodeField';
import { WorkPhotoCapture } from '@/components/work/WorkPhotoCapture';
import { useAuth } from '@/app/AuthProvider';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError } from '@/features/workMode/api';
import type { WorkLookups } from '@/features/workMode/useLookups';
import { useItems } from '@/features/inventory/hooks';
import { useRegisterUnit, uploadUnitPhoto } from '@/features/traceability/hooks';
import type { QcResult } from '@/features/traceability/types';

interface RegisterUnitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lookups: WorkLookups;
  onRegistered?: (id: string) => void;
}

function localDateTimeValue(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const NONE = '__none__';

/** Managers: add a unit that was not logged from a bench (backfill, tablet down). */
export function RegisterUnitDialog({ open, onOpenChange, lookups, onRegistered }: RegisterUnitDialogProps) {
  const { user, profile } = useAuth();
  const { data: items = [] } = useItems();
  const register = useRegisterUnit();

  const [serial, setSerial] = useState('');
  const [itemId, setItemId] = useState('');
  const [builtAt, setBuiltAt] = useState(localDateTimeValue());
  const [builtBy, setBuiltBy] = useState('');
  const [stationId, setStationId] = useState(NONE);
  const [qc, setQc] = useState<QcResult | 'none'>('none');
  const [qcNotes, setQcNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const serializedItems = useMemo(() => {
    const flagged = items.filter((i) => i.is_serialized && i.is_active !== false);
    return flagged.length ? flagged : items.filter((i) => i.kind === 'finished_good');
  }, [items]);

  useEffect(() => {
    if (!open) return;
    setSerial('');
    setItemId(serializedItems.length === 1 ? serializedItems[0].id : '');
    setBuiltAt(localDateTimeValue());
    setBuiltBy(user?.id ?? '');
    setStationId(NONE);
    setQc('none');
    setQcNotes('');
    setNotes('');
    setPhotos([]);
  }, [open, user?.id, serializedItems]);

  const submit = async () => {
    if (!serial.trim()) {
      toast.error('Scan or type the serial number.');
      return;
    }
    if (!itemId) {
      toast.error('Pick which product this unit is.');
      return;
    }
    if (!profile?.organization_id) return;
    setBusy(true);
    try {
      const paths: string[] = [];
      for (const [i, f] of photos.entries()) {
        paths.push(await uploadUnitPhoto(profile.organization_id, serial.trim(), await compressImage(f), `unit-${i + 1}.jpg`));
      }
      const u = await register.mutateAsync({
        serial: serial.trim(),
        itemId,
        builtAt: builtAt ? new Date(builtAt).toISOString() : null,
        builtBy: builtBy || null,
        workstationId: stationId === NONE ? null : stationId,
        photoPaths: paths,
        qcResult: qc === 'none' ? null : qc,
        qcNotes: qcNotes.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success(`${u.serial_number} registered.`);
      onOpenChange(false);
      onRegistered?.(u.id);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="h-5 w-5" />
            Register a unit
          </DialogTitle>
          <DialogDescription>For units that were not logged from a bench. Units built in Work Mode land here on their own.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Serial number</Label>
            <BarcodeField value={serial} onChange={setSerial} autoFocus={open} disabled={busy} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Product</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Pick the finished good" />
                </SelectTrigger>
                <SelectContent>
                  {serializedItems.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name} <span className="font-mono text-xs text-muted-foreground">{i.sku}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-built-at">Built on</Label>
              <Input id="reg-built-at" type="datetime-local" value={builtAt} onChange={(e) => setBuiltAt(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Built by</Label>
              <Select value={builtBy} onValueChange={setBuiltBy}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Who built it" />
                </SelectTrigger>
                <SelectContent>
                  {lookups.peopleList
                    .filter((p) => p.isActive)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Station</Label>
              <Select value={stationId} onValueChange={setStationId}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not recorded</SelectItem>
                  {lookups.stationList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>QC</Label>
              <Select value={qc} onValueChange={(v) => setQc(v as QcResult | 'none')}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not inspected yet</SelectItem>
                  <SelectItem value="pass">Passed</SelectItem>
                  <SelectItem value="fail">Failed — put on hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-qc-notes">QC notes</Label>
              <Input id="reg-qc-notes" value={qcNotes} onChange={(e) => setQcNotes(e.target.value)} className="h-11" placeholder="Optional" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Photos</Label>
            <WorkPhotoCapture files={photos} onChange={setPhotos} max={8} disabled={busy} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reg-notes">Notes</Label>
            <Textarea id="reg-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Why it is being registered by hand, component lots, anything useful." />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Register unit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
